import { useCallback, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { didChangeState, type OperationResult } from '@/shared/api/operationResult'
import { subscriberKeys } from './subscriberKeys'

export interface SubscriberActionState {
  /** Server javob bergan holat (OK / PARTIAL / FAILED) — hammasi HTTP 200 */
  result: OperationResult | null
  /** Haqiqiy nosozlik: tarmoq uzildi, 403, 409, 5xx */
  errorText: string | null
  running: boolean
  run: (action: () => Promise<OperationResult>) => Promise<OperationResult | null>
  reset: () => void
}

/**
 * Boshqaruv amalini bajaradi va IKKI holatni AJRATADI.
 *
 * <h2>⚠️ Nega bitta «xato» yetarli emas</h2>
 * Server rad javobi (`FAILED`) va transport nosozligi butunlay boshqa
 * ish talab qiladi: birinchisida qayta urinish HECH QACHON yordam
 * bermaydi (sabab o'zgarmagan — masalan abonent allaqachon yopilgan),
 * ikkinchisida esa aynan shu kerak. Ikkalasini bitta qizil bannerga
 * yig'ish resellerni tugmani qayta-qayta bosishga undardi
 * (11-SHERIK-KABINETI-UI.md §4.4 dagi bilan ayni qoida).
 *
 * ⚠️ Kesh `didChangeState` bo'yicha bekor qilinadi, `status === 'OK'`
 * bo'yicha EMAS: `PARTIAL` da ham baza yangilangan va ro'yxatdagi
 * holat tegi eskirgan bo'ladi. Faqat `OK` ni tekshirish keng
 * tarqalgan xato (`shared/api/operationResult.ts`).
 *
 * ⚠️ Bekor qilish bitta PREFIKS bo'yicha: bloklash ro'yxatdagi tegni
 * ham, ochiq kartochkadagi holatni ham, paket berish esa balansni
 * ham o'zgartiradi. Kalitlarni birma-bir sanash — yangisi qo'shilganda
 * uni bu yerga qo'shishni unutish, ya'ni ekranda jimgina eskirgan
 * holat qolishi demak.
 */
export function useSubscriberAction(): SubscriberActionState {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [result, setResult] = useState<OperationResult | null>(null)
  const [errorText, setErrorText] = useState<string | null>(null)
  const [running, setRunning] = useState(false)

  const run = useCallback(
    async (action: () => Promise<OperationResult>): Promise<OperationResult | null> => {
      setRunning(true)
      setResult(null)
      setErrorText(null)
      try {
        const outcome = await action()
        setResult(outcome)
        if (didChangeState(outcome)) {
          void queryClient.invalidateQueries({ queryKey: subscriberKeys.all })
        }
        return outcome
      } catch (error) {
        setErrorText(translateError(error, t))
        return null
      } finally {
        setRunning(false)
      }
    },
    [queryClient, t],
  )

  const reset = useCallback(() => {
    setResult(null)
    setErrorText(null)
  }, [])

  return { result, errorText, running, run, reset }
}
